/**
 * MODULE: domain/pages/frontend/utils/pageEditorWidthUtils.ts
 * PURPOSE: Standard vs full-width page layout (persisted per workspace)
 */

export type PageEditorWidthMode = 'standard' | 'full';

const MODES: PageEditorWidthMode[] = ['standard', 'full'];

const widthKey = (workspaceId: string) => `pages-editor-width-${workspaceId}`;

export function isPageEditorWidthMode(value: unknown): value is PageEditorWidthMode {
  return typeof value === 'string' && MODES.includes(value as PageEditorWidthMode);
}

export function readPageEditorWidthMode(workspaceId: string): PageEditorWidthMode {
  try {
    const raw = localStorage.getItem(widthKey(workspaceId));
    return isPageEditorWidthMode(raw) ? raw : 'standard';
  } catch {
    return 'standard';
  }
}

export function writePageEditorWidthMode(workspaceId: string, mode: PageEditorWidthMode): void {
  localStorage.setItem(widthKey(workspaceId), mode);
}

export function togglePageEditorWidthMode(current: PageEditorWidthMode): PageEditorWidthMode {
  return current === 'full' ? 'standard' : 'full';
}

export function pageEditorWidthModeLabel(mode: PageEditorWidthMode): string {
  return mode === 'full' ? 'Standard width' : 'Full width';
}

/** Root scroll container classes for note/database page shells. */
export function pageEditorShellClass(mode: PageEditorWidthMode): string {
  const base = 'relative flex flex-1 flex-col overflow-y-auto';
  if (mode === 'full') {
    return `${base} w-full`;
  }
  return `${base} mx-auto w-full max-w-5xl`;
}

/** Horizontal padding for page sections (header, body, footer). */
export function pageEditorSectionPaddingClass(mode: PageEditorWidthMode): string {
  return mode === 'full' ? 'px-6 lg:px-10' : 'px-8';
}
